/**
 * Async tools
 */

/**
 * A simple sleep async-compatible function
 * @param ms time, in ms, to sleep
 */
export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function mapGather<T, R>(
  elements: T[],
  fn: (e: T) => Promise<R>
): Promise<R[]> {
  const promises = [];
  for (const e of elements) {
    promises.push(fn(e));
  }
  return await Promise.all(promises);
}

/**
 * Like mapGather, but runs at most `concurrency` tasks at once and never
 * rejects: each element resolves to a PromiseSettledResult so a single
 * failure (e.g. a throttled request) doesn't abort the whole batch.
 *
 * @param elements items to map over
 * @param fn async mapper, receives the element and its index
 * @param concurrency maximum number of in-flight tasks
 */
export async function mapPool<T, R>(
  elements: T[],
  fn: (e: T, index: number) => Promise<R>,
  concurrency = 5
): Promise<PromiseSettledResult<R>[]> {
  const results: PromiseSettledResult<R>[] = new Array(elements.length);
  let cursor = 0;
  async function worker() {
    while (cursor < elements.length) {
      const index = cursor++;
      try {
        results[index] = {
          status: "fulfilled",
          value: await fn(elements[index], index),
        };
      } catch (reason) {
        results[index] = { status: "rejected", reason };
      }
    }
  }
  const workerCount = Math.max(1, Math.min(concurrency, elements.length));
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return results;
}

const RETRYABLE_STATUS = new Set([429, 500, 502, 503, 504]);

function getStatusCode(error: unknown): number | undefined {
  const err = error as
    | { response?: { status?: number }; statusCode?: number }
    | undefined;
  return err?.response?.status ?? err?.statusCode;
}

/**
 * Retry an async operation with exponential backoff + jitter, but only for
 * transient/throttling errors (HTTP 429, 5xx). Non-retryable errors are
 * rethrown immediately.
 */
export async function withRetry<R>(
  fn: () => Promise<R>,
  options: { retries?: number; baseDelayMs?: number } = {}
): Promise<R> {
  const retries = options.retries ?? 4;
  const baseDelayMs = options.baseDelayMs ?? 300;
  let attempt = 0;
  for (;;) {
    try {
      return await fn();
    } catch (error) {
      const status = getStatusCode(error);
      const retryable = status !== undefined && RETRYABLE_STATUS.has(status);
      if (!retryable || attempt >= retries) {
        throw error;
      }
      const backoff = baseDelayMs * 2 ** attempt;
      const jitter = Math.random() * baseDelayMs;
      await sleep(backoff + jitter);
      attempt++;
    }
  }
}
